import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import { Button, Stack, Switch, TextField, Typography } from "@mui/material";
import { MarkChatReadOutlined, SpeakerNotesOffOutlined } from "@mui/icons-material";
import { CalendlyEvent } from "../../types/Calendly/CalendlyEvent";
import { hasUserConsent } from "../../services/calendlyService";
import { useCall } from "../../provider/CallProvider";
import { ProductUnion } from "../../types/ProductUnion";

type SoftphoneProps = {
  product: ProductUnion;
  calendlyEvent: CalendlyEvent | null;
};

const Softphone = ({ product, calendlyEvent }: SoftphoneProps) => {
  const { makeCall, setRecordingEnabled, recordingEnabled, cancelRecording, hangUp, phoneError, call, device, status } =
    useCall();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const wasInCallRef = useRef(false);

  const formatDuration = (sec: number) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    const wasInCall = wasInCallRef.current;
    wasInCallRef.current = !!call;

    if (!call && wasInCall) {
      setCooldown(3);
      const timer = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) {
            clearInterval(timer);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [call]);
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (call) {
      setSeconds(0);
      interval = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [call]);

  const formatPhoneNumber = (input: string) => {
    let num = input.trim();
    num = num.replace(/^00/, "+");

    if (num[0] === "+") {
      num = "+" + num.slice(1).replace(/\D/g, "");
    } else {
      num = num.replace(/\D/g, "");
      if (num.startsWith("0")) {
        num = num.slice(1);
      }
      num = "+49" + num;
    }

    return num;
  };

  useEffect(() => {
    setRecordingEnabled((calendlyEvent && hasUserConsent(product.customer, "callRecordingAndSummary")) || false);
  }, [product]);

  useEffect(() => {
    if (product && product.customer && product.customer.telephone) {
      setPhoneNumber(formatPhoneNumber(product.customer.telephone));
    }
  }, [calendlyEvent]);

  return (
    <Grid container>
      <Grid
        sx={{
          mt: 2
        }}
        size={12}>
        <Typography>
          Bitte Telefonnummer der Mandantschaft im Format +49123456789 eingeben und auf “Anrufen” klicken, um den Anruf
          zu starten.
        </Typography>
      </Grid>
      <Grid
        sx={{
          mt: 2
        }}
        size={12}>
        <Stack direction="row" spacing={1} sx={{
          alignItems: "flex-start"
        }}>
          {recordingEnabled && (
            <>
              <MarkChatReadOutlined />
              <Typography>
                Info: Mandantschaft hat Gesprächsaufzeichnung zugestimmt. Das Gespräch wird aufgezeichnet.
              </Typography>
            </>
          )}
          {!recordingEnabled && (
            <>
              <SpeakerNotesOffOutlined />
              <Typography>
                Info: Mandantschaft hat Gesprächsaufzeichnung nicht zugestimmt. Das Gespräch wird nicht aufgezeichnet
              </Typography>
            </>
          )}
        </Stack>
      </Grid>
      <Grid
        sx={{
          mt: 2
        }}
        size={12}>
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <Switch checked={recordingEnabled} onClick={() => cancelRecording()} disabled={!recordingEnabled} />
          {recordingEnabled && (
            <Typography variant="body2">
              Aufzeichnung aktiviert. Bitte nur auf Wunsch der Mandantschaft deaktivieren, wenn diese während des Telefonats der
              Aufzeichnung widerspricht.
            </Typography>
          )}
          {!recordingEnabled && <Typography variant="body2">Aufzeichnung deaktiviert</Typography>}
        </Stack>
      </Grid>
      <Grid
        sx={{
          mt: 2
        }}
        size={12}>
        <div>Status: {status}</div>
        {phoneError && <div style={{ color: "red" }}>Error: {phoneError}</div>}
      </Grid>
      <Grid
        sx={{
          mt: 1
        }}
        size={12}>
        <Typography variant="body1">Gesprächsdauer: {formatDuration(seconds)}</Typography>
      </Grid>
      <Grid
        sx={{
          mt: 2
        }}
        size={12}>
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <TextField
            label="Telefonnummer"
            placeholder="Bitte die Telefonnummer eingeben"
            value={phoneNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
            variant="outlined"
            size={"small"}
            fullWidth
          />
          {!call ? (
            <Button
              variant="contained"
              onClick={() => makeCall(phoneNumber)}
              disabled={!device || !phoneNumber || cooldown > 0}
            >
              {cooldown > 0 ? `Warte ${cooldown}s` : "Anrufen"}
            </Button>
          ) : (
            <>
              <Button variant="contained" color="error" onClick={hangUp}>
                Auflegen
              </Button>
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Softphone;
